import { useContext, useMemo } from "react";
import { Alert, Tag, Typography } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";

import { ICityConfig } from "../../../../interfaces";
import { formatDateStringToDate, formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import { formatCurrency } from "../../../../utils/Masks";
import { ThemeContext } from "../../../../contexts/ThemeContext";

const { Text } = Typography;

interface IProps {
    title: string;
    config: ICityConfig;
}

export default function UpcomingConfigAlert({ config, title }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const hasRewardModule = useMemo(() => {
        return !!config?.nfs_max_amount || !!config?.nfc_max_amount || !!config?.taxe_max_amount;
    }, [config]);

    const hasAtLeastOneInvoiceModule = useMemo(() => {
        return !!config?.nfs_max_month_quantity_by_cnpj || !!config?.nfc_max_month_quantity_by_cnpj;
    }, [config]);

    return (
        <div style={{ marginBottom: 16 }}>
            <Alert
                type="warning"
                showIcon
                icon={<ClockCircleOutlined style={{ fontSize: 18, color: "#faad14" }} />}
                message={
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </div>
                }
                description={
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                            <Text strong style={{ fontSize: 12 }}>Divisores:</Text>
                            {config?.nfs_divider && (
                                <Tag color="blue" style={{ borderRadius: 4 }}>
                                    NFS: {config.nfs_divider}
                                </Tag>
                            )}
                            {config?.nfc_divider && (
                                <Tag color="purple" style={{ borderRadius: 4 }}>
                                    NFC: {config.nfc_divider}
                                </Tag>
                            )}
                            {config?.taxe_divider && (
                                <Tag color="cyan" style={{ borderRadius: 4 }}>
                                    Impostos: {config.taxe_divider}
                                </Tag>
                            )}
                        </div>

                        {config?.tickets_from_full_tax_payment && config?.tickets_from_partial_tax_payment && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                                <Text strong style={{ fontSize: 12 }}>Bilhetes por imposto:</Text>
                                <Tag color="green" style={{ borderRadius: 4 }}>
                                    À vista: {config.tickets_from_full_tax_payment}
                                </Tag>
                                <Tag color="orange" style={{ borderRadius: 4 }}>
                                    Parcelado: {config.tickets_from_partial_tax_payment}
                                </Tag>
                            </div>
                        )}

                        {hasRewardModule && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                                <Text strong style={{ fontSize: 12 }}>Valor Máximo p/ Pontos:</Text>
                                {config?.nfs_max_amount && (
                                    <Tag color="default" style={{ borderRadius: 4 }}>
                                        NFS: R$ {formatCurrency(config.nfs_max_amount.toFixed(2))}
                                    </Tag>
                                )}
                                {config?.nfc_max_amount && (
                                    <Tag color="default" style={{ borderRadius: 4 }}>
                                        NFC: R$ {formatCurrency(config.nfc_max_amount.toFixed(2))}
                                    </Tag>
                                )}
                                {config?.taxe_max_amount && (
                                    <Tag color="default" style={{ borderRadius: 4 }}>
                                        Impostos: R$ {formatCurrency(config.taxe_max_amount.toFixed(2))}
                                    </Tag>
                                )}
                            </div>
                        )}

                        {hasAtLeastOneInvoiceModule && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                                <Text strong style={{ fontSize: 12 }}>Limite mensal por CNPJ:</Text>
                                {config?.nfs_max_month_quantity_by_cnpj && (
                                    <Tag color="default" style={{ borderRadius: 4 }}>
                                        NFS: {config.nfs_max_month_quantity_by_cnpj}
                                    </Tag>
                                )}
                                {config?.nfc_max_month_quantity_by_cnpj && (
                                    <Tag color="default" style={{ borderRadius: 4 }}>
                                        NFC: {config.nfc_max_month_quantity_by_cnpj}
                                    </Tag>
                                )}
                            </div>
                        )}

                        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12, color: isDark ? "#a6a6a6" : "#64748b", marginTop: 4 }}>
                            {config?.description && (
                                <span>
                                    <strong>Descrição: </strong>{config.description}
                                </span>
                            )}
                            <span>
                                <strong>Vigência: </strong>{formatDateStringToDate(config?.effective_date)}
                            </span>
                            {config?.panel_user?.name && (
                                <span>
                                    <strong>Autor: </strong>{config.panel_user.name}
                                </span>
                            )}
                            {config?.created_at && (
                                <span>
                                    <strong>Alterado em: </strong>{formatDateStringToDateAndTime(config.created_at)}
                                </span>
                            )}
                        </div>
                    </div>
                }
                style={{
                    borderRadius: 8,
                }}
            />
        </div>
    );
}
